/**
 * Deployment Token API Service
 *
 * API methods for deployment token operations including listing, creation, and revocation.
 * Updated to use standardized API response format.
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiCollectionResponse } from '../types/api.types';
import type {
  DeploymentTokenSanitized,
  DeploymentTokenCreateDTO,
  DeploymentTokenCreateResponse,
  DeploymentTokenListQuery,
} from '../types/deployment-token.types';

// ============================================================================
// DEPLOYMENT TOKEN SERVICE
// ============================================================================

export const deploymentTokenService = {
  /**
   * List all deployment tokens for an application
   */
  listDeploymentTokens: async (
    tenantId: string,
    applicationId: string,
    query?: DeploymentTokenListQuery
  ): Promise<DeploymentTokenSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<DeploymentTokenSanitized>>(
      `/tenants/${tenantId}/applications/${applicationId}/deployment-tokens`,
      { params: query }
    );
    return response.data.data;
  },

  /**
   * Get deployment token by ID
   */
  getDeploymentToken: async (
    tenantId: string,
    applicationId: string,
    tokenId: string
  ): Promise<DeploymentTokenSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<DeploymentTokenSanitized>>(
      `/tenants/${tenantId}/applications/${applicationId}/deployment-tokens/${tokenId}`
    );
    return response.data.data;
  },

  /**
   * Create a new deployment token
   * Returns the raw token (shown only once!)
   */
  createDeploymentToken: async (
    tenantId: string,
    applicationId: string,
    data: DeploymentTokenCreateDTO
  ): Promise<DeploymentTokenCreateResponse> => {
    const response = await apiClient.post<ApiSuccessResponse<DeploymentTokenCreateResponse>>(
      `/tenants/${tenantId}/applications/${applicationId}/deployment-tokens`,
      data
    );
    return response.data.data;
  },

  /**
   * Revoke a deployment token
   * Token is marked as revoked, not deleted (for audit trail)
   */
  revokeDeploymentToken: async (
    tenantId: string,
    applicationId: string,
    tokenId: string
  ): Promise<DeploymentTokenSanitized> => {
    const response = await apiClient.delete<ApiSuccessResponse<DeploymentTokenSanitized>>(
      `/tenants/${tenantId}/applications/${applicationId}/deployment-tokens/${tokenId}`
    );
    return response.data.data;
  },
};
